import Cookies from 'js-cookie';

/**
 * Centralized cookie service for authentication
 * Mimics the functionality of ebook frontend's API routes
 */

// Cookie names used across AristAI applications
export const COOKIE_NAMES = {
  SESSION: '_aristai_session',
  ID_TOKEN: '_aristai_id',
  REFRESH_TOKEN: '_aristai_refresh_token',
  SERVER_REFRESH_TOKEN: '_aristai_server_refresh_token'
};

// Get domain based on environment
const getDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined; // No domain for localhost
  }
  return '.aristai.io';
};

/**
 * Cookie configuration following ebook pattern
 */
const getCookieOptions = (options = {}) => {
  const domain = getDomain();
  const baseOptions = {
    secure: window.location.protocol === 'https:',
    sameSite: 'none',
    ...options
  };
  
  if (domain) {
    baseOptions.domain = domain;
  }
  
  return baseOptions;
};

/**
 * Equivalent of ebook's /api/cookies route
 * For React SPA, we need to work around HttpOnly cookies
 * In dev mode, we try both approaches
 */
export const getAuthCookies = async () => {
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  
  try {
    // First try: Direct cookie access (works for non-HttpOnly cookies)
    const directCookies = {
      [COOKIE_NAMES.SESSION]: Cookies.get(COOKIE_NAMES.SESSION),
      [COOKIE_NAMES.ID_TOKEN]: Cookies.get(COOKIE_NAMES.ID_TOKEN),
      [COOKIE_NAMES.REFRESH_TOKEN]: Cookies.get(COOKIE_NAMES.REFRESH_TOKEN),
      [COOKIE_NAMES.SERVER_REFRESH_TOKEN]: Cookies.get(COOKIE_NAMES.SERVER_REFRESH_TOKEN)
    };
    
    // Check if we got any cookies
    const hasAnyCookies = Object.values(directCookies).some(value => value);
    
    if (hasAnyCookies) {
      if (isDevMode) {
        console.log('✅ Direct cookie access successful:', directCookies);
      }
      return directCookies;
    }
    
    // Second try: Call backend API route to read HttpOnly cookies
    if (isDevMode) {
      console.log('⚠️ No direct cookies found, attempting backend cookie reading...');
    }
    
    try {
      const response = await fetch('/api/cookies', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-cache',
      });
      
      if (response.ok) {
        const apiCookies = await response.json();
        if (isDevMode) {
          console.log('✅ Backend cookie reading successful:', apiCookies);
        }
        
        // Convert API response to our format
        return {
          [COOKIE_NAMES.SESSION]: apiCookies.session,
          [COOKIE_NAMES.ID_TOKEN]: apiCookies.idToken,
          [COOKIE_NAMES.REFRESH_TOKEN]: apiCookies.refreshToken,
          [COOKIE_NAMES.SERVER_REFRESH_TOKEN]: apiCookies.serverRefreshToken
        };
      }
    } catch (apiError) {
      if (isDevMode) {
        console.log('⚠️ Backend cookie reading failed (expected for React SPA):', apiError.message);
      }
    }
    
    // Third try: Check for mock user in dev mode
    if (isDevMode && window.mockAuthUser) {
      console.log('🧪 Using mock authentication for testing');
      return {
        [COOKIE_NAMES.SESSION]: 'mock-session',
        [COOKIE_NAMES.ID_TOKEN]: 'mock-id-token',
        [COOKIE_NAMES.REFRESH_TOKEN]: 'mock-refresh-token',
        [COOKIE_NAMES.SERVER_REFRESH_TOKEN]: 'mock-server-refresh-token'
      };
    }
    
    // No cookies found
    return {
      [COOKIE_NAMES.SESSION]: null,
      [COOKIE_NAMES.ID_TOKEN]: null,
      [COOKIE_NAMES.REFRESH_TOKEN]: null,
      [COOKIE_NAMES.SERVER_REFRESH_TOKEN]: null
    };
    
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error('Error reading authentication cookies:', error);
    }
    return {
      [COOKIE_NAMES.SESSION]: null,
      [COOKIE_NAMES.ID_TOKEN]: null,
      [COOKIE_NAMES.REFRESH_TOKEN]: null,
      [COOKIE_NAMES.SERVER_REFRESH_TOKEN]: null
    };
  }
};

/**
 * Get specific authentication cookie
 */
export const getAuthCookie = (cookieName) => {
  try {
    return Cookies.get(cookieName);
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error(`Error reading cookie ${cookieName}:`, error);
    }
    return null;
  }
};

/**
 * Set authentication cookie with proper security settings
 */
export const setAuthCookie = (name, value, options = {}) => {
  try {
    const cookieOptions = getCookieOptions({
      expires: options.expires || 50/1440, // 50 minutes default (like ebook)
      ...options
    });
    
    Cookies.set(name, value, cookieOptions);
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.log(`Cookie ${name} set successfully`);
    }
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error(`Error setting cookie ${name}:`, error);
    }
    return false;
  }
};

/**
 * Equivalent of ebook's /api/refresh route functionality
 * Updates ID token cookie after refresh
 */
export const updateIdTokenCookie = (newIdToken, options = {}) => {
  try {
    const cookieOptions = getCookieOptions({
      expires: options.expires || 50/1440, // 50 minutes like ebook
      httpOnly: false, // Client-side accessible (React limitation)
      ...options
    });
    
    Cookies.set(COOKIE_NAMES.ID_TOKEN, newIdToken, cookieOptions);
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.log('ID token cookie updated successfully');
    }
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error('Error updating ID token cookie:', error);
    }
    return false;
  }
};

/**
 * Equivalent of ebook's /api/auth/signout route
 * Clears all authentication cookies with proper domain settings
 */
export const clearAllAuthCookies = () => {
  try {
    const domain = getDomain();
    const clearOptions = {
      domain,
      secure: window.location.protocol === 'https:',
      sameSite: 'none'
    };
    
    // Remove domain for localhost
    if (!domain) {
      delete clearOptions.domain;
    }
    
    // Clear all 4 authentication cookies (like ebook)
    Object.values(COOKIE_NAMES).forEach(cookieName => {
      try {
        // Try multiple clearing strategies
        Cookies.remove(cookieName, clearOptions);
        Cookies.remove(cookieName); // Fallback without options
        if (domain) {
          Cookies.remove(cookieName, { domain }); // Domain-specific fallback
        }
      } catch (error) {
        if (process.env.REACT_APP_DEV_MODE === 'true') {
          console.warn(`Failed to clear cookie ${cookieName}:`, error);
        }
      }
    });
    
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.log('All authentication cookies cleared');
    }
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error('Error clearing authentication cookies:', error);
    }
    return false;
  }
};

/**
 * Check if user is authenticated based on required cookies
 */
export const isAuthenticated = () => {
  const cookies = getAuthCookies();
  return !!(cookies[COOKIE_NAMES.SESSION] && cookies[COOKIE_NAMES.ID_TOKEN]);
};

/**
 * Get current user info from ID token (if valid JWT)
 */
export const getCurrentUserFromToken = () => {
  try {
    const idToken = getAuthCookie(COOKIE_NAMES.ID_TOKEN);
    if (!idToken) return null;
    
    // Decode JWT payload (basic decode, not verification)
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return {
      uid: payload.uid || payload.user_id,
      email: payload.email,
      name: payload.name,
      exp: payload.exp,
      // Add other fields as needed
    };
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error('Error decoding ID token:', error);
    }
    return null;
  }
};

/**
 * Check if ID token is expired
 */
export const isTokenExpired = () => {
  try {
    const user = getCurrentUserFromToken();
    if (!user || !user.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime >= user.exp;
  } catch (error) {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.error('Error checking token expiration:', error);
    }
    return true;
  }
};

export default {
  getAuthCookies,
  getAuthCookie,
  setAuthCookie,
  updateIdTokenCookie,
  clearAllAuthCookies,
  isAuthenticated,
  getCurrentUserFromToken,
  isTokenExpired,
  COOKIE_NAMES
};